class NewCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.overlay = null;
        this.container = null;
        this.closeBtn = null;
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this._refreshing = false;
        this.sectionId = 'new-header';
    }

    initSubscriptionDropdowns() {
        this.querySelectorAll('.subscription-dropdown').forEach(dropdown => {
            if ((dropdown.getAttribute('data-initialized') || '') === 'true') return;

            const toggle = dropdown.querySelector('.subscription-dropdown__toggle');
            const menu = dropdown.querySelector('.subscription-dropdown__menu');
            const options = dropdown.querySelectorAll('.subscription-dropdown__option');

            if (!toggle || !menu) return;

            toggle.addEventListener('click', event => {
                event.stopPropagation();
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                this.closeAllDropdowns();
                toggle.setAttribute('aria-expanded', (!isOpen).toString());
                if (isOpen) {
                    menu.setAttribute('hidden', '');
                } else {
                    menu.removeAttribute('hidden');
                }
            });

            menu.addEventListener('click', event => event.stopPropagation());

            options.forEach(option => {
                option.addEventListener('click', event => {
                    event.stopPropagation();
                    const label = toggle.querySelector('span:not(.subscription-dropdown__arrow)');
                    if (label) {
                        label.textContent = option.textContent;
                    }
                    toggle.setAttribute('aria-expanded', 'false');
                    menu.setAttribute('hidden', '');

                    const lineIndex = parseInt(option.getAttribute('data-line-index') || '0');
                    const sellingPlanId = option.getAttribute('data-selling-plan-id');
                    if (lineIndex && sellingPlanId) {
                        this.changeLineSellingPlan(lineIndex, Number(sellingPlanId));
                    }
                });
            });

            dropdown.setAttribute('data-initialized', 'true');
        });
    }

    initUpgradeSubscriptionDropdowns() {
        this.querySelectorAll('.normal-subscription-dropdown').forEach(dropdown => {
            if ((dropdown.getAttribute('data-initialized') || '') === 'true') return;

            const toggle = dropdown.querySelector('.normal-subscription-dropdown__toggle');
            const menu = dropdown.querySelector('.normal-subscription-dropdown__menu');
            const options = dropdown.querySelectorAll('.normal-subscription-dropdown__image');

            if (toggle && menu) {
                const starterVariantIdAttr = toggle.getAttribute('data-starter-variant-id');
                if (starterVariantIdAttr) {
                    toggle.addEventListener('click', async (event) => {
                        event.stopPropagation();
                        const lineIndex = parseInt(toggle.getAttribute('data-line-index') || '0');
                        const starterVariantId = Number(starterVariantIdAttr);
                        const starterPlanIdAttr = toggle.getAttribute('data-starter-selling-plan-id');
                        const starterSellingPlanId = starterPlanIdAttr ? Number(starterPlanIdAttr) : 0;
                        if (!lineIndex || !starterVariantId) return;
                        try {
                            toggle.setAttribute('disabled', '');
                            await this.upgradeToStarterKit(lineIndex, starterVariantId, starterSellingPlanId);
                        } catch (e) {
                            console.error('Upgrade to Starter Kit failed:', e);
                        } finally {
                            toggle.removeAttribute('disabled');
                        }
                    });
                    dropdown.setAttribute('data-initialized', 'true');
                    return;
                }
                toggle.addEventListener('click', async (event) => {
                    event.stopPropagation();
                    const bestPlanId = toggle.getAttribute('data-best-plan-id');
                    const lineIndex = parseInt(toggle.getAttribute('data-line-index') || '0');
                    if (bestPlanId && lineIndex) {
                        try {
                            toggle.setAttribute('disabled', '');
                            await this.changeLineSellingPlan(lineIndex, Number(bestPlanId));
                            await this.refreshDrawerAndOpenDropdown(lineIndex);
                        } catch (e) {
                            console.error('Upgrade to subscription failed:', e);
                        } finally {
                            toggle.removeAttribute('disabled');
                        }
                        return;
                    }
                    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                    this.closeAllDropdowns();
                    toggle.setAttribute('aria-expanded', (!isOpen).toString());
                    if (isOpen) {
                        menu.setAttribute('hidden', '');
                    } else {
                        menu.removeAttribute('hidden');
                    }
                });
                menu.addEventListener('click', (e) => e.stopPropagation());
            }

            options.forEach((opt) => {
                opt.addEventListener('click', (event) => {
                    event.stopPropagation();
                    const lineIndex = parseInt(opt.getAttribute('data-line-index') || '0');
                    const sellingPlanId = opt.getAttribute('data-selling-plan-id');
                    if (lineIndex && sellingPlanId) {
                        this.changeLineSellingPlan(lineIndex, Number(sellingPlanId));
                    }
                    if (toggle && menu) {
                        toggle.setAttribute('aria-expanded', 'false');
                        menu.setAttribute('hidden', '');
                    }
                });
            });

            dropdown.setAttribute('data-initialized', 'true');
        });
    }

    handleOutsideClick(event) {
        if (!event.target.closest('.subscription-dropdown') && !event.target.closest('.normal-subscription-dropdown')) {
            this.closeAllDropdowns();
        }
    }

    async fetchFreshRoot() {
        const url = `${window.location.pathname}?section_id=${this.sectionId}`;
        const resp = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!resp.ok) return null;
        const html = await resp.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.querySelector('new-cart-drawer');
    }

    replaceDrawerSections(freshRoot) {
        // Copy dataset attributes from fresh root to current element
        if (freshRoot && freshRoot.dataset) {
            Object.keys(freshRoot.dataset).forEach((k) => {
                this.dataset[k] = freshRoot.dataset[k];
            });
        }
        // Replace entire inner HTML so all parts update together
        this.innerHTML = freshRoot.innerHTML;
    }

    bindStaticListeners() {
        this.overlay = this.querySelector('.new-cart-drawer__overlay');
        this.container = this.querySelector('.new-cart-drawer__container');
        this.closeBtn = this.querySelector('.new-cart-drawer__close-button, .new-cart-drawer__close');

        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.close());
        }
        if (this.overlay) {
            this.overlay.addEventListener('click', (event) => {
                event.stopPropagation();
                this.close();
            });
        }
    }

    reInitUI() {
        this.bindStaticListeners();
        this.initSubscriptionDropdowns();
        this.initUpgradeSubscriptionDropdowns();
        this.initQuantityButtons();
        this.initUpgradeButton();
    }

    initUpgradeButton() {
        const upgradeBtn = this.querySelector('[data-upgrade-atc]');
        if (!upgradeBtn) return;
        
        upgradeBtn.removeAttribute('data-initialized');
        
        const scripts = this.querySelectorAll('script');
        scripts.forEach(script => {
            if (script.textContent.includes('data-upgrade-atc')) {
                try {
                    const scriptFunc = new Function(script.textContent);
                    scriptFunc();
                } catch (e) {
                    console.error('Error reinitializing upgrade button:', e);
                }
            }
        });
    }

    async refreshDrawerAndOpenDropdown(lineIndex) {
        try {
            const prevPercent = this.getCurrentProgressPercentage();
            const freshRoot = await this.fetchFreshRoot();
            if (!freshRoot) return;
            this.replaceDrawerSections(freshRoot);
            this.reInitUI();
            this._prevProgressPercentage = prevPercent;
            const cartTotal = parseInt(this.dataset.cartTotal || '0') || 0;
            this.updateProgressBar(cartTotal, true);

            const item = this.querySelector(`.new-cart-drawer__item[data-line-index="${lineIndex}"]`);
            if (item) {
                const toggle = item.querySelector('.subscription-dropdown__toggle');
                const menu = item.querySelector('.subscription-dropdown__menu');
                if (toggle && menu) {
                    this.closeAllDropdowns();
                    toggle.setAttribute('aria-expanded', 'true');
                    menu.removeAttribute('hidden');
                }
            }
        } catch (err) {
            console.error('Failed to refresh cart drawer:', err);
        }
    }

    async refreshCartDrawer() {
        if (this._refreshing) return;
        this._refreshing = true;
        try {
            const prevPercent = this.getCurrentProgressPercentage();
            const freshRoot = await this.fetchFreshRoot();
            if (!freshRoot) return;
            this.replaceDrawerSections(freshRoot);
            this.reInitUI();
            this._prevProgressPercentage = prevPercent;
            const cartTotal = parseInt(this.dataset.cartTotal || '0') || 0;
            this.updateProgressBar(cartTotal, true);
            this.updateUpgradeContainerVisibility();
            // Limpiar cualquier loading state que pueda quedar
            this.clearAllLoadingStates();
        } catch (e) {
            console.error('Failed to refresh cart drawer:', e);
        } finally {
            this._refreshing = false;
        }
    }

    updateUpgradeContainerVisibility() {
        const upgradeContainer = this.querySelector('[data-cart-upgrade-container]');
        if (!upgradeContainer) return;
        
        const itemsContainer = this.querySelector('.new-cart-drawer__item-container');
        const hasItems = itemsContainer && itemsContainer.children.length > 0;
        
        // Check if upgrade product is already in cart
        const upgradeProductId = upgradeContainer.getAttribute('data-upgrade-product-id');
        let productInCart = false;
        
        if (upgradeProductId) {
            const cartItems = this.querySelectorAll('.new-cart-drawer__item');
            cartItems.forEach(item => {
                const itemProductId = item.getAttribute('data-product-id');
                if (itemProductId === upgradeProductId) {
                    productInCart = true;
                }
            });
        }
        
        if (!hasItems || productInCart) {
            upgradeContainer.setAttribute('data-hidden', 'true');
        } else {
            upgradeContainer.removeAttribute('data-hidden');
        }
    }

    async requestRefresh(openAfter = false) {
        await this.refreshCartDrawer();
        if (openAfter) this.open();
    }

    closeAllDropdowns() {
        this.querySelectorAll('.subscription-dropdown__menu').forEach(menu => menu.setAttribute('hidden', ''));
        this.querySelectorAll('.subscription-dropdown__toggle').forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
        this.querySelectorAll('.normal-subscription-dropdown__menu').forEach(menu => menu.setAttribute('hidden', ''));
        this.querySelectorAll('.normal-subscription-dropdown__toggle').forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
    }

    connectedCallback() {
        this.bindStaticListeners();

        document.addEventListener('click', (e) => {
            const link = e.target.closest('a[href="/cart"]');
            if (!link) return;
            e.preventDefault();
            this.open();
        }, true);

        this.initSubscriptionDropdowns();
        this.initUpgradeSubscriptionDropdowns();
        this.initQuantityButtons();
        this.initUpgradeButton();
        document.addEventListener('click', this.handleOutsideClick);

        document.addEventListener('cart:updated', () => {
            this.requestRefresh(false);
        });

        // Custom events to refresh and open drawer from PDP or elsewhere
        document.addEventListener('cart:refresh-open', async () => {
            await this.requestRefresh(true);
        });
        document.addEventListener('cart:refresh', async () => {
            await this.requestRefresh(false);
        });
    }

    open() {
        this.removeAttribute('hidden');
        requestAnimationFrame(() => {
            this.classList.add('new-cart-drawer--open');
        });
        this.closeAllDropdowns();
        
        document.body.style.overflow = 'hidden';
        //document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = `-${window.scrollY}px`;
        
        // Verificar visibilidad del upgrade container
        this.updateUpgradeContainerVisibility();
        
        // Animar la barra de progreso al abrir el drawer
        const cartTotal = parseInt(this.dataset.cartTotal || '0') || 0;
        // Pequeño delay para que el drawer se abra primero, luego animar la barra
        setTimeout(() => {
            this._prevProgressPercentage = 0; // Empezar desde 0 para animación inicial
            this.updateProgressBar(cartTotal, true);
        }, 100);
    }

    close() {
        this.classList.remove('new-cart-drawer--open');

        const scrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
        
        setTimeout(() => this.setAttribute('hidden', ''), 300);
    }

    updateCart(cart) {
        // No DOM patching here; global refresh handles DOM updates
        this.updateProgressBar(cart.total_price);
    }

    updateProgressBar(cartTotal, animate = false) {
        const freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold) || 9900;
        const hasSubscription = this.dataset.hasSubscription === 'true';
        
        let remainingAmount, progressPercentage;
        
        if (hasSubscription) {

            remainingAmount = 0;
            progressPercentage = 100;
        } else {
            remainingAmount = freeShippingThreshold - cartTotal;
            progressPercentage = Math.min((cartTotal / freeShippingThreshold) * 100, 100);
        }

        const progressFill = this.querySelector('.free-shipping-bar__fill');
        const progressText = this.querySelector('.free-shipping-bar__text');

        if (progressFill) {
            // Keep the icon visible at 0 by toggling an empty state
            const isEmpty = progressPercentage <= 0;
            progressFill.setAttribute('data-empty', isEmpty ? 'true' : 'false');
            
            // También marcar el track para CSS
            const track = this.querySelector('.free-shipping-bar__track');
            if (track) {
                track.setAttribute('data-empty', isEmpty ? 'true' : 'false');
            }

            if (animate && typeof this._prevProgressPercentage === 'number') {
                const from = Math.max(0, Math.min(this._prevProgressPercentage, 100));
                this._prevProgressPercentage = undefined;
                progressFill.classList.add('no-animate');
                progressFill.setAttribute('style', `width: ${from}%`);
                requestAnimationFrame(() => {
                    progressFill.classList.remove('no-animate');
                    progressFill.setAttribute('style', `width: ${progressPercentage}%`);
                });
            } else {
                progressFill.setAttribute('style', `width: ${progressPercentage}%`);
            }
        }

        if (progressText) {
            if (hasSubscription) {
                progressText.textContent = "Free Shipping included with subscription!";
            } else if (remainingAmount > 0) {
                const formattedAmount = this.formatMoney(remainingAmount);
                progressText.textContent = `Spend ${formattedAmount} more for Free Shipping`;
            } else {
                progressText.textContent = "You've unlocked Free Shipping!";
            }
        }
    }

    formatMoney(cents) {
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
    }

    initQuantityButtons() {
        this.querySelectorAll('.qty-btn--plus, .qty-btn--minus').forEach(btn => {
            btn.addEventListener('click', (e) => this.handleQuantityChange(e));
        });
    }

    async handleQuantityChange(event) {
        const button = event.currentTarget;
        const lineIndex = parseInt(button.dataset.lineIndex);
        const currentQty = parseInt(button.dataset.quantity);
        const isPlus = button.classList.contains('qty-btn--plus');
        const newQty = isPlus ? currentQty + 1 : Math.max(0, currentQty - 1);

        const itemContainer = button.closest('.new-cart-drawer__item');
        if (itemContainer) {
            this.showItemLoading(itemContainer);
        }

        this.clearErrorMessages();

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    line: lineIndex,
                    quantity: newQty
                })
            });

            if (!response.ok) {
                const errorMessage = await this.parseCartError(response, 
                    isPlus 
                        ? 'Could not increase the product quantity'
                        : 'Could not update the product quantity'
                );
                
                this.showErrorMessage(errorMessage, itemContainer);
                throw new Error(errorMessage);
            }

            const cart = await response.json();
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart }
            }));
        } catch (error) {
            console.error('Error updating quantity:', error);
            
            // Verificar si el error es de sold out y eliminar el item automáticamente
            if (this.isSoldOutError(error.message)) {
                console.log('Sold out error detected, removing item from cart...');
                
                try {
                    const removed = await this.removeItemFromCart(lineIndex);
                    if (removed) {
                        // Mostrar mensaje informativo en lugar de error
                        this.showErrorMessage('Product was removed from cart because it\'s no longer available.', itemContainer);
                        return; // Salir temprano, no mostrar el error original
                    }
                } catch (removeError) {
                    console.error('Failed to remove sold out item:', removeError);
                }
            }
            
            if (itemContainer) {
                this.hideItemLoading(itemContainer);
            }
            
            if (!document.querySelector('.cart-error-modal')) {
                this.showErrorMessage('Error updating quantity. Try again.', itemContainer);
            }
        }
    }

    getLineItemQuantity(...args) {
        const lineIndex = Number(args[0] || 0);
        try {
            const root = this.querySelector(`.new-cart-drawer__item[data-line-index="${lineIndex}"]`);
            const q = root ? root.querySelector('.qty-number') : null;
            const val = q ? parseInt(q.textContent || '0') : 0;
            return isNaN(val) ? 0 : val;
        } catch {
            return 0;
        }
    }

    async changeLineSellingPlan(...args) {
        const lineIndex = Number(args[0] || 0);
        const sellingPlanId = Number(args[1] || 0);
        const quantity = this.getLineItemQuantity(lineIndex) || 1;
        
        const itemContainer = this.querySelector(`.new-cart-drawer__item[data-line-index="${lineIndex}"]`);
        if (itemContainer) {
            this.showItemLoading(itemContainer);
        }
        
        this.clearErrorMessages();
        
        try {
            console.log('Attempting to change selling plan:', { lineIndex, sellingPlanId, quantity });
            
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    line: lineIndex,
                    quantity: quantity,
                    selling_plan: sellingPlanId
                })
            });

            if (!response.ok) {
                const errorMessage = await this.parseCartError(response, 
                    sellingPlanId > 0 
                        ? 'Could not change to subscription' 
                        : 'Could not change purchase type'
                );
                
                console.log('Parsed error message:', errorMessage);
                this.showErrorMessage(errorMessage, itemContainer);
                throw new Error(errorMessage);
            }

            const cart = await response.json();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (err) {
            console.error('Error updating selling plan:', err);
            console.log('Error type:', typeof err, 'Error message:', err.message);
            
            // Verificar si el error es de sold out y eliminar el item automáticamente
            if (this.isSoldOutError(err.message)) {
                console.log('Sold out error detected in selling plan change, removing item from cart...');
                
                try {
                    const removed = await this.removeItemFromCart(lineIndex);
                    if (removed) {
                        // Mostrar mensaje informativo en lugar de error
                        this.showErrorMessage('Product was removed from cart because it\'s no longer available.', itemContainer);
                        return; // Salir temprano, no mostrar el error original
                    }
                } catch (removeError) {
                    console.error('Failed to remove sold out item:', removeError);
                }
            }
            
            if (itemContainer) {
                this.hideItemLoading(itemContainer);
            }
            
            const hasModal = document.querySelector('.cart-error-modal');
            
            if (!hasModal) {
                const genericMessage = sellingPlanId > 0 
                    ? 'Error changing to subscription. Try again.' 
                    : 'Error changing purchase type. Try again.';
                console.log('Showing generic message:', genericMessage);
                this.showErrorMessage(genericMessage, itemContainer);
            }
        }
    }

    getCurrentProgressPercentage() {
        try {
            const track = this.querySelector('.free-shipping-bar__track');
            const fill = this.querySelector('.free-shipping-bar__fill');
            if (!track || !fill) return 0;
            const rectTrack = track.getBoundingClientRect();
            const rectFill = fill.getBoundingClientRect();
            const width = Math.max(0, Math.min(rectFill.width, rectTrack.width));
            return rectTrack.width ? Math.round((width / rectTrack.width) * 100) : 0;
        } catch {
            return 0;
        }
    }

    showItemLoading(itemContainer) {
        // Remover overlay existente si existe
        this.hideItemLoading(itemContainer);
        
        // Crear overlay con spinner
        const overlay = document.createElement('div');
        overlay.className = 'cart-item-loading-overlay';
        overlay.innerHTML = `
            <div class="cart-item-spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#F6BE00" stroke-width="2" stroke-linecap="round" stroke-dasharray="60" stroke-dashoffset="60">
                        <animate attributeName="stroke-dashoffset" dur="1s" values="60;0" repeatCount="indefinite"/>
                    </circle>
                </svg>
            </div>
        `;
        
        itemContainer.style.position = 'relative';
        itemContainer.appendChild(overlay);
    }

    hideItemLoading(itemContainer) {
        const overlay = itemContainer.querySelector('.cart-item-loading-overlay');
        if (overlay) {
            overlay.remove();
        }
    }

    clearAllLoadingStates() {
        const overlays = this.querySelectorAll('.cart-item-loading-overlay');
        overlays.forEach(overlay => overlay.remove());
    }

    showErrorMessage(message, itemContainer = null) {
        if (!message || message.trim() === '') {
            message = 'An unexpected error has occurred';
        }
        
        console.log('Showing error message:', message);
        
        this.clearErrorMessages();
        
        const errorModal = document.createElement('div');
        errorModal.className = 'cart-error-modal';
        errorModal.innerHTML = `
            <div class="cart-error-modal__overlay"></div>
            <div class="cart-error-modal__content">
                <div class="cart-error-modal__header">
                    <div class="cart-error-modal__icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#FEF2F2" stroke="#DC2626" stroke-width="2"/>
                            <path d="M15 9L9 15M9 9L15 15" stroke="#DC2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                    <h3 class="cart-error-modal__title">Error en el carrito</h3>
                    <button class="cart-error-modal__close" type="button">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 4L4 12M4 4L12 12" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="cart-error-modal__body">
                    <p class="cart-error-modal__message"></p>
                </div>
                <div class="cart-error-modal__footer">
                    <button class="cart-error-modal__button" type="button">Understood</button>
                </div>
            </div>
        `;
        
        // Insertar el mensaje de forma segura
        const messageElement = errorModal.querySelector('.cart-error-modal__message');
        if (messageElement) {
            messageElement.textContent = message;
            console.log('Error message displayed in modal:', message);
        } else {
            console.error('Could not find message element in modal');
        }
        
        // Agregar event listeners
        const overlay = errorModal.querySelector('.cart-error-modal__overlay');
        const closeBtn = errorModal.querySelector('.cart-error-modal__close');
        const okBtn = errorModal.querySelector('.cart-error-modal__button');
        
        const closeModal = () => {
            errorModal.classList.add('cart-error-modal--closing');
            setTimeout(() => this.clearErrorMessages(), 200);
        };
        
        // Agregar event listeners con validación
        if (overlay) {
            overlay.addEventListener('click', closeModal);
        }
        if (closeBtn) {
            closeBtn.addEventListener('click', closeModal);
        }
        if (okBtn) {
            okBtn.addEventListener('click', closeModal);
        }
        
        // Cerrar con ESC
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEsc);
            }
        };
        document.addEventListener('keydown', handleEsc);
        
        // Auto-ocultar después de 8 segundos (más tiempo para modal)
        setTimeout(closeModal, 8000);
        
        // Agregar al body para que esté por encima de todo
        document.body.appendChild(errorModal);
        
        // Trigger animation
        requestAnimationFrame(() => {
            errorModal.classList.add('cart-error-modal--open');
        });
    }

    clearErrorMessages() {
        const errorMessages = this.querySelectorAll('.cart-drawer-error-message');
        errorMessages.forEach(msg => msg.remove());
        
        const errorModals = document.querySelectorAll('.cart-error-modal');
        errorModals.forEach(modal => modal.remove());
    }

    async parseCartError(response, defaultMessage = 'An unexpected error has occurred') {
        try {
            const errorData = await response.json().catch(() => null);
            
            if (errorData) {
                if (errorData.message) {
                    return errorData.message;
                }
                if (errorData.description) {
                    return errorData.description;
                }
                if (errorData.error) {
                    return errorData.error;
                }
                if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    return errorData.errors[0];
                }
                if (errorData.errors && typeof errorData.errors === 'object') {
                    const firstError = Object.values(errorData.errors)[0];
                    if (Array.isArray(firstError)) {
                        return firstError[0];
                    }
                    return firstError;
                }
            }
            
            if (response.status === 422) {
                return 'No enough inventory available for this product';
            } else if (response.status === 400) {
                return 'Invalid request. Verify product data';
            } else if (response.status === 404) {
                return 'Product not found';
            } else if (response.status >= 500) {
                return 'Error of the server. Try again in a moment';
            }
            
            return defaultMessage;
        } catch (e) {
            console.error('Error parsing cart error:', e);
            return defaultMessage;
        }
    }

    isSoldOutError(errorMessage) {
        if (!errorMessage || typeof errorMessage !== 'string') return false;
        
        const soldOutKeywords = [
            'no enough inventory',
            'not enough inventory',
            'insufficient inventory',
            'out of stock',
            'sold out',
            'no stock',
            'inventory not available',
            'product is not available'
        ];
        
        const lowerMessage = errorMessage.toLowerCase();
        return soldOutKeywords.some(keyword => lowerMessage.includes(keyword));
    }

    async removeItemFromCart(lineIndex) {
        try {
            console.log(`Removing item from cart at line index: ${lineIndex}`);
            
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    line: lineIndex,
                    quantity: 0
                })
            });

            if (!response.ok) {
                console.error('Failed to remove item from cart:', response.status);
                return false;
            }

            const cart = await response.json();
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart }
            }));
            
            console.log('Item successfully removed from cart');
            return true;
        } catch (error) {
            console.error('Error removing item from cart:', error);
            return false;
        }
    }

    async upgradeToStarterKit(lineIndex, starterVariantId, starterSellingPlanId = 0) {
        const quantity = this.getLineItemQuantity(lineIndex) || 1;
        
        const itemContainer = this.querySelector(`.new-cart-drawer__item[data-line-index="${lineIndex}"]`);
        if (itemContainer) {
            this.showItemLoading(itemContainer);
        }
        
        this.clearErrorMessages();
        
        try {
            const removeResp = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: lineIndex, quantity: 0 })
            });
            
            if (!removeResp.ok) {
                const errorMessage = await this.parseCartError(removeResp, 'Could not remove the current product');
                this.showErrorMessage(errorMessage, itemContainer);
                throw new Error(errorMessage);
            }

            const addResp = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [
                        {
                            id: Number(starterVariantId),
                            quantity,
                            ...(starterSellingPlanId ? { selling_plan: Number(starterSellingPlanId) } : {})
                        }
                    ]
                })
            });
            
            if (!addResp.ok) {
                const errorMessage = await this.parseCartError(addResp, 'Could not add Starter Kit');
                
                this.showErrorMessage(errorMessage, itemContainer);
                throw new Error(errorMessage);
            }

            try {
                const cart = await addResp.json();
                if (cart && cart.items) {
                    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
                }
            } catch (parseError) {
                console.error('Error parsing cart response:', parseError);
            }
        } catch (error) {
            console.error('Error upgrading to Starter Kit:', error);
            
            if (itemContainer) {
                this.hideItemLoading(itemContainer);
            }
            
            if (!document.querySelector('.cart-error-modal')) {
                this.showErrorMessage('Error upgrading to Starter Kit. Try again.', itemContainer);
            }
            
            throw error;
        }
    }

}

customElements.define('new-cart-drawer', NewCartDrawer);